import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import Cleave from "cleave.js/react";
import { toast } from "react-toastify";
import {
  Alert,
  Button,
  Col,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalHeader,
  Row,
  Spinner,
} from "reactstrap";

import { tokenizeCreditCard } from "../../../helpers/asaasEncryption";
import {
  MyTenantSubscription,
  createPaddleCustomerPortal,
  updateSubscriptionPaymentMethod,
} from "../../../services/subscriptionService";

interface PaymentMethodCardProps {
  subscription: MyTenantSubscription;
  onUpdated: (subscription: MyTenantSubscription) => void;
}

const isPaddleSubscription = (subscription: MyTenantSubscription): boolean =>
  subscription.gateway_slug === "paddle" ||
  subscription.payment_method === "paddle" ||
  subscription.currency === "USD";

const PaymentMethodCard: React.FC<PaymentMethodCardProps> = ({
  subscription,
  onUpdated,
}) => {
  const { t } = useTranslation();
  const [modalOpen, setModalOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [portalLoading, setPortalLoading] = useState(false);
  const [cardNumber, setCardNumber] = useState("");
  const [cardName, setCardName] = useState("");
  const [cardCpf, setCardCpf] = useState("");
  const [cardExpiry, setCardExpiry] = useState("");
  const [cardCvv, setCardCvv] = useState("");
  const [error, setError] = useState("");

  // Paddle USD: cartão/cancelamento no Customer Portal (sem formulário Asaas).
  if (isPaddleSubscription(subscription)) {
    const handleOpenPortal = async (): Promise<void> => {
      setPortalLoading(true);

      try {
        const response = await createPaddleCustomerPortal();
        const portalUrl = response.data?.portal_url?.trim();

        if (!portalUrl) {
          toast.error(t("settings.subscription.paymentMethod.paddlePortalError"));
          return;
        }

        window.open(portalUrl, "_blank", "noopener,noreferrer");
        toast.success(
          response.message || t("settings.subscription.paymentMethod.paddlePortalOpened")
        );
      } catch (portalError: any) {
        toast.error(
          portalError?.data || t("settings.subscription.paymentMethod.paddlePortalError")
        );
      } finally {
        setPortalLoading(false);
      }
    };

    return (
      <div className="border rounded p-3 mb-4">
        <div className="d-flex flex-wrap align-items-center justify-content-between gap-2">
          <div>
            <p className="text-muted mb-1">{t("settings.subscription.paymentMethod.title")}</p>
            <p className="fw-medium mb-1">
              {t("settings.subscription.paymentMethod.paddleManaged")}
            </p>
            <p className="text-muted small mb-0">
              {t("settings.subscription.paymentMethod.paddlePortalHint")}
            </p>
          </div>
          <Button
            color="soft-primary"
            size="sm"
            outline
            onClick={handleOpenPortal}
            disabled={portalLoading}
          >
            {portalLoading ? (
              <Spinner size="sm" />
            ) : (
              t("settings.subscription.paymentMethod.paddlePortalCta")
            )}
          </Button>
        </div>
      </div>
    );
  }

  const last4 = subscription.payment_method_last4;
  const showCard =
    subscription.status === "active" &&
    subscription.payment_method === "credit_card" &&
    Boolean(last4);

  if (!showCard && subscription.status !== "past_due" && subscription.status !== "suspended") {
    return null;
  }

  const handleSubmit = async (): Promise<void> => {
    const digits = cardNumber.replace(/\D/g, "");

    if (
      digits.length < 13 ||
      !cardName.trim() ||
      cardExpiry.trim() === "" ||
      cardCvv.trim().length < 3
    ) {
      setError(t("settings.subscription.checkout.card.invalid"));
      return;
    }

    setSubmitting(true);
    setError("");

    try {
      const holderInfo = {
        name: cardName.trim(),
        ...(cardCpf.trim() ? { cpfCnpj: cardCpf.replace(/\D/g, "") } : {}),
      };

      const tokenized = await tokenizeCreditCard({
        number: cardNumber,
        holderName: cardName,
        expiry: cardExpiry,
        ccv: cardCvv,
        holderInfo,
      });

      const response = await updateSubscriptionPaymentMethod({
        credit_card_token: tokenized.creditCardToken,
        credit_card_last4: tokenized.creditCardLast4,
        credit_card_holder_info: holderInfo,
      });

      onUpdated(response.data);
      toast.success(response.message || t("settings.subscription.paymentMethod.updated"));
      setModalOpen(false);
      setCardNumber("");
      setCardName("");
      setCardCpf("");
      setCardExpiry("");
      setCardCvv("");
    } catch (submitError: any) {
      if (submitError?.message === "INVALID_EXPIRY") {
        setError(t("settings.subscription.checkout.card.invalid"));
      } else {
        setError(submitError?.data || t("settings.subscription.paymentMethod.error"));
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <div className="border rounded p-3 mb-4">
        <div className="d-flex flex-wrap align-items-center justify-content-between gap-2">
          <div>
            <p className="text-muted mb-1">{t("settings.subscription.paymentMethod.title")}</p>
            {showCard ? (
              <p className="fw-medium mb-0">
                {t("settings.subscription.paymentMethod.cardMasked", { last4 })}
              </p>
            ) : (
              <p className="text-muted mb-0">{t("settings.subscription.paymentMethod.none")}</p>
            )}
          </div>
          <Button color="soft-primary" size="sm" outline onClick={() => setModalOpen(true)}>
            {t("settings.subscription.paymentMethod.change")}
          </Button>
        </div>
      </div>

      <Modal isOpen={modalOpen} toggle={() => !submitting && setModalOpen(false)} centered>
        <ModalHeader toggle={() => !submitting && setModalOpen(false)}>
          {t("settings.subscription.paymentMethod.changeTitle")}
        </ModalHeader>
        <ModalBody>
          {error ? <Alert color="danger">{error}</Alert> : null}
          <Row className="g-3">
            <Col md={12}>
              <Label>{t("settings.subscription.checkout.card.number")}</Label>
              <Cleave
                className="form-control"
                options={{ creditCard: true }}
                value={cardNumber}
                onChange={(event: React.ChangeEvent<HTMLInputElement> & { target: { rawValue: string } }) =>
                  setCardNumber(event.target.rawValue)
                }
                placeholder={t("settings.subscription.checkout.card.numberPlaceholder")}
                disabled={submitting}
                autoComplete="cc-number"
              />
            </Col>
            <Col md={12}>
              <Label>{t("settings.subscription.checkout.card.name")}</Label>
              <Input
                value={cardName}
                onChange={(event) => setCardName(event.target.value)}
                disabled={submitting}
                autoComplete="cc-name"
              />
            </Col>
            <Col md={6}>
              <Label>{t("settings.subscription.checkout.card.expiry")}</Label>
              <Cleave
                className="form-control"
                options={{
                  date: true,
                  datePattern: ["m", "y"],
                }}
                value={cardExpiry}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                  setCardExpiry(event.target.value)
                }
                placeholder={t("settings.subscription.checkout.card.expiryPlaceholder")}
                disabled={submitting}
                autoComplete="cc-exp"
              />
            </Col>
            <Col md={6}>
              <Label>{t("settings.subscription.checkout.card.cvv")}</Label>
              <Cleave
                className="form-control"
                options={{
                  blocks: [4],
                  numericOnly: true,
                }}
                value={cardCvv}
                onChange={(event: React.ChangeEvent<HTMLInputElement> & { target: { rawValue: string } }) =>
                  setCardCvv(event.target.rawValue)
                }
                placeholder={t("settings.subscription.checkout.card.cvvPlaceholder")}
                type="password"
                disabled={submitting}
                autoComplete="cc-csc"
              />
            </Col>
            <Col md={12}>
              <Label>{t("settings.subscription.checkout.card.cpf")}</Label>
              <Input
                value={cardCpf}
                onChange={(event) => setCardCpf(event.target.value)}
                disabled={submitting}
              />
            </Col>
          </Row>
          <div className="d-flex justify-content-end gap-2 mt-4">
            <Button color="light" onClick={() => setModalOpen(false)} disabled={submitting}>
              {t("settings.subscription.checkout.actions.cancel")}
            </Button>
            <Button color="primary" onClick={handleSubmit} disabled={submitting}>
              {submitting ? <Spinner size="sm" /> : t("settings.subscription.paymentMethod.save")}
            </Button>
          </div>
        </ModalBody>
      </Modal>
    </>
  );
};

export default PaymentMethodCard;
